import { fetchRatesForBedbankOffersById } from 'actions/BedbankOfferActions'
import { fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import { fetchAvailableRatesForOffer, fetchOfferById, fetchOffersById } from 'actions/OfferActions'
import { checkoutAccommodationOfferView, getAccommodationItems } from 'checkout/selectors/view/accommodation'
import { CHECKOUT_ITEM_TYPE_LE_HOTEL } from 'constants/checkout'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_VILLA } from 'constants/offer'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { unique } from 'lib/array/arrayUtils'
import { isLEHotelItem, isTourV1Item } from 'lib/checkout/checkoutUtils'
import { useEffect } from 'react'
import { shallowEqual } from 'react-redux'
import { getBundledFlightItems } from 'checkout/selectors/view/flights'

function isBedbankItem(item: App.Checkout.AccommodationItem): item is App.Checkout.BedbankHotelItem {
  return item.itemType === 'bedbankHotel'
}

export default function useCheckoutOfferData(stateOverride?: App.State) {
  const dispatch = useAppDispatch()

  const {
    regionCode,
    currencyCode,
    existingOrder,
    accommodationItems,
    bundledFlightItems,
    offerViews,
    offersMap,
  } = useAppSelector((currentState) => {
    const state = stateOverride ?? currentState
    return {
      regionCode: state.checkout.cart.regionCode,
      currencyCode: state.checkout.cart.currencyCode,
      existingOrder: state.checkout.cart.existingOrder,
      accommodationItems: getAccommodationItems(state),
      bundledFlightItems: getBundledFlightItems(state),
      offerViews: checkoutAccommodationOfferView(state),
      offersMap: state.offer.offers,
    } }, shallowEqual)

  const flightOrigin = bundledFlightItems[0]?.originAirportCode

  // Get LE hotel and tourV1 offer data
  useEffect(() => {
    const leHotelItems = accommodationItems.filter(isLEHotelItem)
    const tourV1Items = accommodationItems.filter(isTourV1Item)
    const offerIds = [
      ...unique([
        ...leHotelItems.map(item => item.offerId),
        ...tourV1Items.map(item => item.offerId),
      ]),
      ...existingOrder ? existingOrder.items.map(item => item.offerId) : [],
    ]
    offerIds.forEach((offerId) => dispatch(fetchOfferById(offerId)))
  }, [dispatch, accommodationItems, existingOrder])

  // Get LE hotel calendar data
  useEffect(() => {
    const hotelViews = offerViews.data.filter(view =>
      (
        view.offerType === CHECKOUT_ITEM_TYPE_LE_HOTEL ||
        view.offerType === OFFER_TYPE_LAST_MINUTE ||
        view.offerType === OFFER_TYPE_ALWAYS_ON ||
        view.offerType === OFFER_TYPE_VILLA
      ) &&
      view.reservationType === RESERVATION_TYPE_INSTANT_BOOKING,
    )

    for (const view of hotelViews) {
      const { offerId, occupancy: occupants, startDate: checkIn, endDate: checkOut } = view
      if (offersMap[view.offerId]) {
        const uniqueKeys = new Set(view.itemViews.map((itemView: App.Checkout.AccommodationItemView) => itemView.uniqueKey))
        const pkgFilter = (pkg: App.Package) => uniqueKeys.has(pkg.uniqueKey)

        dispatch(fetchCalendarsByOccupancy(offerId, {
          occupants,
          packages: offersMap[view.offerId].packages.filter(pkgFilter),
          regionCode,
          flightOrigin,
        }))
      }
      if (view.offerType === 'tactical_ao_hotel' && view.offerLoaded && checkIn && checkOut) {
        occupants.forEach(occupant => {
          dispatch(fetchAvailableRatesForOffer(
            offersMap[view.offerId], {
              occupants: [occupant],
              checkIn,
              checkOut,
              currencyCode,
              flightOrigin,
            },
          ))
        })
      }
    }
  }, [currencyCode, dispatch, flightOrigin, offerViews, offersMap, regionCode])

  // Get Bedbank offer data
  useEffect(() => {
    const bedbankItems = accommodationItems.filter(isBedbankItem)
    const bedbankOfferIds = unique<string>(bedbankItems.map(item => item.offerId))
    dispatch(fetchOffersById(bedbankOfferIds))

    // Fetch availability/pricing
    for (const offerView of offerViews.data) {
      const { offerId, occupancy: occupants, startDate: checkIn, endDate: checkOut } = offerView
      if (offerView.offerType === 'bedbank_hotel' && checkIn && checkOut) {
        dispatch(fetchRatesForBedbankOffersById([offerId], occupants, checkIn, checkOut))
      }
    }
  }, [dispatch, accommodationItems, offerViews])
}
